import { 
  UserProfile, 
  SecurityScore, 
  AlertItem, 
  ProtectedDocument, 
  FamilyMember, 
  SubscriptionPlan, 
  AuditLogItem, 
  AdminAnalytics,
  RecoveryCase
} from '../types';
import { RECOVERY_STEPS_MASTER } from './recoverySteps';

export const INITIAL_USER: UserProfile = {
  id: 'usr_au_98124',
  firstName: 'Alex',
  lastName: 'Mitchell',
  email: 'alex.mitchell@sydney-design.com.au',
  phone: '+61 412 890 341',
  country: 'Australia',
  marketingConsent: false,
  role: 'user',
  mfaEnabled: true,
  createdAt: '2026-01-15T08:30:00Z',
  lastLoginAt: '2026-08-20T11:45:00Z',
  avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80'
};

export const INITIAL_SECURITY_SCORE: SecurityScore = {
  overallScore: 82,
  status: 'good',
  headline: 'Your identity security is good, but there are areas that need attention.',
  lastCalculated: '2026-08-20T10:15:00Z',
  categories: {
    email: {
      id: 'email',
      name: 'Email Security',
      score: 90,
      maxScore: 100,
      status: 'good',
      summary: 'Strong security with 2FA enabled on primary Gmail account.',
      recommendation: 'Check third-party app access permissions quarterly.',
      actionUrl: '/account-security',
      actionText: 'Review Email Security',
      details: ['App-based 2FA active', 'Recovery phone verified', 'No unusual forwarding rules detected']
    },
    password: {
      id: 'password',
      name: 'Password Security',
      score: 78,
      maxScore: 100,
      status: 'attention',
      summary: '2 reused passwords detected in secondary retail services.',
      recommendation: 'Update reused passwords using a password manager.',
      actionUrl: '/account-security',
      actionText: 'Update Reused Passwords',
      details: ['Password manager vault in use', 'No known master vault exposure', '2 duplicate passwords flagged']
    },
    account: {
      id: 'account',
      name: 'Account Security',
      score: 85,
      maxScore: 100,
      status: 'good',
      summary: '8 of 10 key Australian financial & government accounts secured.',
      recommendation: 'Enable hardware/app MFA on your superannuation portal.',
      actionUrl: '/account-security',
      actionText: 'Secure Remaining Accounts',
      details: ['myGov Digital Identity linked', 'NetBank 2FA active', 'Super account using SMS OTP']
    },
    idDocuments: {
      id: 'idDocuments',
      name: 'ID Documents',
      score: 88,
      maxScore: 100,
      status: 'good',
      summary: '2 documents protected and monitored (NSW Licence, Australian Passport).',
      recommendation: 'Add your Medicare card to document monitoring.',
      actionUrl: '/identity-documents',
      actionText: 'Manage Documents',
      details: ['NSW Driver Licence Card Number monitored', 'Passport expiry tracked', 'No active DVS misuse detected']
    },
    scamExposure: {
      id: 'scamExposure',
      name: 'Scam Exposure',
      score: 80,
      maxScore: 100,
      status: 'good',
      summary: 'Low recent scam risk. 1 suspicious SMS checked and cleared this week.',
      recommendation: 'Always scan unexpected parcel delivery messages before clicking.',
      actionUrl: '/scam-checker',
      actionText: 'Check a Message',
      details: ['No reported credential submissions', 'Scam awareness checks passed']
    },
    identityExposure: {
      id: 'identityExposure',
      name: 'Identity Exposure',
      score: 72,
      maxScore: 100,
      status: 'attention',
      summary: 'Email found in 2 past historical corporate data breaches.',
      recommendation: 'Review the latest breach alert and confirm passwords were changed.',
      actionUrl: '/alerts',
      actionText: 'Review Breach Alerts',
      details: ['Found in 2024 Ticketmaster breach', 'Found in 2019 Canva breach', 'Passwords already rotated']
    },
    creditMonitoring: {
      id: 'creditMonitoring',
      name: 'Credit Monitoring',
      score: 92,
      maxScore: 100,
      status: 'good',
      summary: 'No unauthorized credit enquiries detected across Equifax & Experian.',
      recommendation: 'Keep credit alerts enabled for instant loan inquiry notifications.',
      actionUrl: '/dashboard',
      actionText: 'View Credit Activity',
      details: ['Clean credit enquiry record', 'No court judgements or defaults', 'Real-time bureau webhook active']
    }
  }
};

export const INITIAL_ALERTS: AlertItem[] = [
  {
    id: 'alt-001',
    userId: 'usr_au_98124',
    title: 'New Data Breach Detected: Ticketing Provider',
    category: 'breach',
    severity: 'MEDIUM',
    date: '2026-08-18T14:22:00Z',
    organisation: 'Global Ticketing Platform (Ticketmaster)',
    whatHappened: 'A massive external data breach was confirmed involving customer purchase history and encrypted tokens. Your monitored email (alex.mitchell@sydney-design.com.au) was present in the dataset.',
    exposedInformation: ['Email Address', 'Full Name', 'Mobile Number', 'Purchase History'],
    recommendedAction: 'Verify that you do not reuse your ticketing password on banking or email services. Enable multi-factor authentication on your account.',
    isResolved: false
  },
  {
    id: 'alt-002',
    userId: 'usr_au_98124',
    title: 'Suspicious Toll SMS Phishing Campaign Alert',
    category: 'scam',
    severity: 'LOW',
    date: '2026-08-15T09:10:00Z',
    organisation: 'National Scamwatch Notice',
    whatHappened: 'High volume of fraudulent Linkt and Transurban overdue toll SMS messages targeting NSW and VIC mobile numbers with fake payment links.',
    exposedInformation: ['None - Informational Warning'],
    recommendedAction: 'Do not click toll payment links sent via text message. Pay tolls only through official provider apps or websites directly.',
    isResolved: true,
    resolvedAt: '2026-08-16T10:00:00Z'
  },
  {
    id: 'alt-003',
    userId: 'usr_au_98124',
    title: 'Identity Document Monitored: NSW Driver Licence Verified',
    category: 'document',
    severity: 'LOW',
    date: '2026-08-01T11:00:00Z',
    organisation: 'Service NSW / DVS',
    whatHappened: 'Your NSW Driver Licence was successfully linked for active card number monitoring and security alerts.',
    exposedInformation: ['None'],
    recommendedAction: 'No action required. Continuous monitoring is active.',
    isResolved: true,
    resolvedAt: '2026-08-01T11:05:00Z'
  }
];

export const INITIAL_DOCUMENTS: ProtectedDocument[] = [
  {
    id: 'doc-01',
    documentType: 'driver_licence',
    name: 'NSW Driver Licence',
    state: 'NSW',
    lastFourDigits: '8912',
    expiryDate: '2029-04-14',
    protectionStatus: 'protected',
    lastChecked: '2026-08-20T08:00:00Z',
    notes: 'Primary personal driver licence. DVS card number monitoring active.'
  },
  {
    id: 'doc-02',
    documentType: 'passport',
    name: 'Australian Passport',
    lastFourDigits: '4401',
    expiryDate: '2032-11-20',
    protectionStatus: 'protected',
    lastChecked: '2026-08-20T08:00:00Z',
    notes: 'Adult 10-year passport issued by DFAT Sydney.'
  }
];

export const INITIAL_FAMILY: FamilyMember[] = [
  {
    id: 'fam-01',
    relationship: 'Me',
    name: 'Alex Mitchell',
    email: 'alex.mitchell@sydney-design.com.au',
    phone: '+61 412 890 341',
    securityScore: 82,
    activeAlertsCount: 1,
    monitoringStatus: 'active',
    consentGranted: true,
    avatarColor: 'bg-emerald-600'
  },
  {
    id: 'fam-02',
    relationship: 'Partner',
    name: 'Emma Mitchell',
    email: 'emma.m@creative-hub.com.au',
    phone: '+61 423 119 845',
    securityScore: 88,
    activeAlertsCount: 0,
    monitoringStatus: 'active',
    consentGranted: true,
    avatarColor: 'bg-indigo-600'
  },
  {
    id: 'fam-03',
    relationship: 'Child',
    name: 'Oliver Mitchell (14 yrs)',
    email: 'oliver.m2012@outlook.com',
    securityScore: 74,
    activeAlertsCount: 1,
    monitoringStatus: 'active',
    consentGranted: true,
    avatarColor: 'bg-amber-600'
  },
  {
    id: 'fam-04',
    relationship: 'Parent',
    name: 'Robert Mitchell (72 yrs)',
    email: 'robert.mitchell1954@bigpond.com',
    phone: '+61 408 552 199',
    securityScore: 68,
    activeAlertsCount: 2,
    monitoringStatus: 'active',
    consentGranted: true,
    avatarColor: 'bg-purple-600'
  }
];

export const INITIAL_PLANS: SubscriptionPlan[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Essential identity & scam checks for every Australian.',
    monthlyPrice: 0,
    annualPrice: 0,
    features: [
      'Identity exposure check',
      'AI Scam message checker',
      'Website safety analyzer',
      'Identity Security Score (0-100)',
      'Basic Australian recovery guides',
      'ID Watermark Generator'
    ],
    ctaText: 'Get Started Free'
  },
  {
    id: 'personal',
    name: 'Personal Protection',
    tagline: 'Continuous surveillance & rapid response for individuals.',
    monthlyPrice: 9.95,
    annualPrice: 99.00,
    isPopular: true,
    features: [
      'Everything in Free',
      '24/7 Continuous identity monitoring',
      'Real-time data breach alerts (SMS & Email)',
      'Australian Driver Licence & Passport monitoring',
      'Personalized 8-step recovery wizard',
      'Priority Australian fraud expert assistance',
      'Dark web credential scanning'
    ],
    ctaText: 'Start Personal Protection'
  },
  {
    id: 'family',
    name: 'Family Protection',
    tagline: 'Comprehensive multi-member security for up to 5 relatives.',
    monthlyPrice: 19.95,
    annualPrice: 199.00,
    features: [
      'Everything in Personal',
      'Protection for up to 5 family members',
      'Unified Family Security Dashboard',
      'Individual privacy & adult consent controls',
      'Elderly parent scam vulnerability shielding',
      'Child email & gaming account exposure alerts',
      'Shared family recovery emergency plans'
    ],
    ctaText: 'Start Family Protection'
  }
];

export const INITIAL_RECOVERY_CASE: RecoveryCase = {
  id: 'case-au-2026-091',
  incidentType: 'data_breach',
  incidentTitle: 'Ticketmaster & Telecommunications Data Exposure',
  createdAt: '2026-08-19T09:30:00Z',
  status: 'in_progress',
  progressPercentage: 50,
  completedStepsCount: 4,
  totalStepsCount: 8,
  steps: RECOVERY_STEPS_MASTER.map((step, idx) => ({
    ...step,
    isCompleted: idx < 4,
    completedAt: idx < 4 ? '2026-08-19T10:00:00Z' : undefined
  })),
  caseNotes: 'Primary email secured with 2FA and passwords updated on Google and banking. Credit ban request submitted to Equifax.'
};

export const INITIAL_AUDIT_LOGS: AuditLogItem[] = [
  {
    id: 'log-101',
    timestamp: '2026-08-20T11:45:00Z',
    actor: 'Alex Mitchell (alex.mitchell@sydney-design.com.au)',
    action: 'User Session Login (MFA Verified)',
    category: 'auth',
    ipAddress: '110.142.78.21',
    location: 'Sydney, NSW, Australia',
    details: 'Successful biometric passkey authentication on macOS Chrome.'
  },
  {
    id: 'log-102',
    timestamp: '2026-08-20T10:15:00Z',
    actor: 'System Engine',
    action: 'Identity Security Score Recalculated',
    category: 'security',
    ipAddress: '127.0.0.1',
    location: 'Sydney AWS ap-southeast-2',
    details: 'Score calculated: 82/100 across 7 categories.'
  },
  {
    id: 'log-103',
    timestamp: '2026-08-18T14:22:00Z',
    actor: 'Breach Detection Pipeline',
    action: 'Breach Alert Generated (alt-001)',
    category: 'identity',
    ipAddress: '127.0.0.1',
    location: 'Sydney AWS ap-southeast-2',
    details: 'Matched monitored email against Ticketmaster 2024 dataset.'
  },
  {
    id: 'log-104',
    timestamp: '2026-08-15T09:10:00Z',
    actor: 'Threat Intelligence Feed',
    action: 'National Scam Warning Broadcast',
    category: 'security',
    ipAddress: '127.0.0.1',
    location: 'Canberra, ACT',
    details: 'Linkt SMS Phishing threat bulletin synchronized from Scamwatch.'
  }
];

export const INITIAL_ANALYTICS: AdminAnalytics = {
  registeredUsers: 14850,
  activeUsers: 9420,
  identityChecks: 84200,
  scamChecks: 41900,
  websiteChecks: 27800,
  alertsGenerated: 18450,
  activeAlerts: 2180,
  resolvedAlerts: 16270,
  subscriptions: {
    free: 11200,
    personal: 2680,
    family: 970,
    mrrAud: 46019.50,
    churnRatePercent: 1.8,
    conversionRatePercent: 24.5
  }
};
